import React, { useState, useEffect } from 'react';
import { ChevronDown } from 'lucide-react';
import { Button } from './ui/button';
import { logoUrl } from '../data/mock';
import { useLanguage } from '../context/LanguageContext';

const HeroSection = () => {
  const { t } = useLanguage();
  const [logoOpacity, setLogoOpacity] = useState(1);

  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;
      const fadeEnd = 400;
      if (scrollY <= 0) {
        setLogoOpacity(1);
      } else if (scrollY >= fadeEnd) {
        setLogoOpacity(0.3);
      } else {
        const opacity = 1 - (scrollY / fadeEnd) * 0.7;
        setLogoOpacity(Math.max(0.3, opacity));
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleScrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section
      id="pocetna"
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
    >
      {/* Background with Parallax Effect */}
      <div
        className="absolute inset-0 bg-cover bg-center bg-fixed"
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1654097801176-cb1795fd0c5e?w=1920&q=80')`,
        }}
      />
      
      {/* Dark Overlay */}
      <div className="absolute inset-0 bg-black/70" />
      
      {/* Gradient Overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-transparent to-black/80" />

      {/* Content */}
      <div className="relative z-10 text-center px-6 max-w-4xl mx-auto">
        {/* Logo with gentle float and golden hover effect */}
        <div 
          className="animate-slide-up mb-6 transition-opacity duration-500 group cursor-pointer"
          style={{ opacity: logoOpacity }}
        >
          <img
            src={logoUrl}
            alt="Studio 149 - Muški Frizer"
            className="h-[140px] md:h-[195px] lg:h-[250px] w-auto mx-auto animate-gentle-float transition-all duration-500 group-hover:drop-shadow-golden"
          />
        </div>
        
        {/* H1 - Main SEO heading */}
        <h1 className="text-lg md:text-xl text-gray-300 mb-16 font-light tracking-wide animate-slide-up-delay">
          {t.hero.subtitle}
        </h1>

        {/* CTA Buttons */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-6 animate-fade-in-delay">
          <a
            href="tel:+381629729671"
            className="inline-flex items-center justify-center bg-[#0067ff] hover:bg-[#0052cc] text-white px-10 py-4 text-sm uppercase tracking-widest transition-all duration-300 hover:scale-105 rounded-md font-medium"
            data-testid="hero-cta-call"
          >
            {t.hero.cta1}
          </a>
          <Button
            variant="outline"
            onClick={() => handleScrollToSection('usluge')}
            className="border-white text-white hover:bg-white hover:text-black px-10 py-6 text-sm uppercase tracking-widest transition-all duration-300"
            data-testid="hero-cta-services"
          >
            {t.hero.cta2}
          </Button>
        </div>
      </div>

      {/* Scroll Down Indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
        <button
          onClick={() => handleScrollToSection('o-nama')}
          className="text-white/70 hover:text-white transition-colors duration-300 flex flex-col items-center"
        >
          <span className="text-xs uppercase tracking-widest mb-2">{t.hero.scrollDown}</span>
          <ChevronDown size={24} />
        </button>
      </div>
    </section>
  );
};

export default HeroSection;
